import type { CentralIconBaseProps } from "central-icons/CentralIconBase";
import type { FC } from "react";
import type {
  SubscriptionInterval,
  SubscriptionLevel,
  SubscriptionPlanLimits,
  UsageData,
} from "@/stores/subscription-store/subscription-store.types";
import type { RouteDefinition } from "@/wayfinder";

export type ApiErrorResponse = {
  message?: string;
  errors?: Record<string, string[]>;
};

export type UserTeamSubscriptionsEssentials = {
  user_has_collective_team_subscriptions: boolean;
  default_team: {
    user_is_workspace_admin: boolean;
    team_id: number | null;
    subscription_id: number | null;
    subscription_is_collective: boolean;
    subscription_has_stripe_id: boolean;
  };
};

export type UserPreferences = {
  copy_include_children: boolean;
};

export type Auth = {
  user: User | null;
  userTeamSubscriptionsEssentials: UserTeamSubscriptionsEssentials;
  preferences: UserPreferences;
};

// biome-ignore lint/style/noEnum: Consistent way of handling strings
export enum AuthProcess {
  LogIn = "log_in",
  Register = "register",
  DeleteAccount = "delete_account",
}

export type BreadcrumbItem = {
  title: string;
  route?: RouteDefinition<"get"> | undefined;
  href?: string | undefined;
};

export type NavGroup = {
  title: string;
  items: NavItem[];
};

export type NavItem = {
  title: string;
  route?: RouteDefinition<"get"> | undefined;
  href?: string | undefined;
  icon?: FC<CentralIconBaseProps> | null;
  isActive?: boolean;
};

export type SharedDataLocalization = {
  locale: string;
  language: string;
  fallbackLanguage: string;
  languageOptions: Language[];
  timezone: string;
};

export type SharedDataSubscriptions = {
  trialDays: number;
};

export type SharedDataReferrals = {
  discountAmount: string | null;
};

export type RecentSession = {
  date: string;
  sessions: ClientSession[];
};

export type RecentSessions = RecentSession[];

export type SystemAlertLevel = "critical" | "warning" | "info";

export type SystemAlert = {
  id: number;
  message: string;
  level: SystemAlertLevel;
  is_dismissable: boolean;
};

export type SharedDataConfig = {
  localization: SharedDataLocalization;
  subscriptions: SharedDataSubscriptions;
  referrals: SharedDataReferrals;
  maxAudioFileUploadSizeMb: number;
  acceptedMediaUploadMimeTypes: string[];
  idleSessionCheckIntervalInMs: number;
  intendedUrl: string;
  marketingSiteUrl: string;
  applicationEmailAddress: string;
  knowledgeHubUrl: string | null;
  termsOfServiceUrl: string | null;
  privacyPolicyUrl: string | null;
  appVersion: string | null;
};

export type SharedData = {
  name: string;
  assistantName: string;
  quote: { message: string; author: string };
  auth: Auth;
  recentSessions?: RecentSessions;
  config: SharedDataConfig;
  flash: {
    info: string | null;
    success: string | null;
    error: string | null;
  };
  onboarding?: OnboardingProgress | null;
  systemAlert?: SystemAlert | null;
  [key: string]: unknown;
};

export type MedicalCode = {
  id: number;
  code: string;
  name: string;
  label: string | null;
  description?: string;
  type: "icd10" | "kva";
  pivot?: {
    is_approved?: boolean;
  };
};

export type Summary = {
  id: number;
  has_no_content: boolean;
  structured_journal: JournalSection[];
  transcription: string;
  content_excerpt: string;
  created_at: string;
  index: number;
};

export const ClientSessionStatuses = {
  RECORDING: "recording",
  PROCESSING: "processing",
  PROCESSED: "processed",
  EXPORTED: "exported",
  FAILED: "failed",
  PROCESSING_FAILED: "processing_failed",
} as const;

export type ClientSessionStatusesKeys = keyof typeof ClientSessionStatuses;
export type ClientSessionStatusesValues =
  (typeof ClientSessionStatuses)[ClientSessionStatusesKeys];

export type ClientSessionTemplate = {
  id: number;
  name: string;
  is_active: boolean;
  professional_role_id: number;
};

export type ClientSession = {
  id: number;
  title: string | null;
  display_title: string | null;
  summary_generation_status: string | null;
  latest_summary_id: number | null;
  transcription: string;
  content_excerpt: string;
  created_at: string;
  exported_at: string | null;
  medical_codes?: MedicalCode[];
  medical_codes_attempted?: boolean;
  completion_message_attempted?: boolean;
  structured_journal?: JournalSection[];
  session_documents?: SessionDocument[];
  summaries: Summary[];
  summaries_count: number;
  status: ClientSessionStatusesValues;
  professional_role_id: number | null;
  session_template_id: number | null;
  structured_journal_configuration_id: number | null;
  gpt_model: string | null;
  professional_role?: ProfessionalRole;
  session_template?: ClientSessionTemplate;
};

export type User = {
  id: number;
  name: string;
  email: string;
  locale: string;
  language: string;
  timezone: string;
  avatar?: string;
  email_verified_at: string | null;
  last_logged_in_at: string | null;
  created_at: string;
  updated_at: string;
  default_team_id: number;
  structured_journal_configuration_id: number;
  external_identifier: string;
  teams: PivotTeam[];
  professional_roles?: ProfessionalRole[];
  primary_authentication_method?: string;
  [key: string]: unknown; // This allows for additional properties...
};

export type Team = {
  id: number;
  created_by_user_id: number;
  name: string;
  slug: string;
  url: string | null;
  email: string;
  description: string | null;
  users_count?: number | undefined;
  client_session_retention_in_days: number;
  created_at: string;
  updated_at: string;
  users?: User[];
};

export type PivotTeam = Team & {
  pivot: {
    user_id: number;
    team_id: number;
    subscription_id: number;
    team_user_invite_id: number;
    role: string;
  };
};

export type TeamUserInvite = {
  id: number;
  team_id: number;
  email: string;
  invitation_url: string | null;
  invitation_url_valid_until: string | null;
  accepted_invitation_at: string | null;
};

export type TeamUser = User & { pivot?: { role?: string } };

export type NullableInteger = number | null;

export type SubscriptionUsageData = {
  can_add_user_to_team: UsageData | null;
  can_add_client_session: UsageData | null;
  can_add_custom_template: UsageData | null;
};

export type SubscriptionLicences = {
  available: number;
  used: number;
};

export type SubscriptionTrial = {
  is_active: boolean;
  ends_at: string | null;
  days_remaining: number | null;
};

export type Subscription = {
  id: number | null;
  stripe_id: string | null;
  level: SubscriptionLevel | null;
  is_active: boolean;
  is_grace_period: boolean;
  activated_at: string | null;
  current_period_ends_at: string | null;
  subscription_ends_at: string | null;
  subscription_product_price: SubscriptionProductPrice | null;
  usage: SubscriptionUsageData;
  licences: SubscriptionLicences;
  trial: SubscriptionTrial;
  custom_price_per_quantity: {
    price_as_string: number | string | null;
    price_with_currency: string | null;
  };
} & SubscriptionPlanLimits;

export type SubscriptionItem = {
  product: SubscriptionProduct;
  price: SubscriptionProductPrice;
};

// The product one subscribes to (`Premium Subscription`, `Super Duper Premium Subscription`, etc.)
export type SubscriptionProduct = {
  id: NullableInteger;
  label: string;
  description: string;
};

// The specific price of the subscription product (`$29/month`, `$99/year`, etc.)
export type SubscriptionProductPrice = {
  id: number;
  label: string;
  description?: string | null;
  is_default: boolean;
  stripe_price_id: string | null;
  price: number;
  price_with_currency: string;
  monthly_price: number;
  monthly_price_with_currency: string;
  currency_code: string;
  currency_symbol: string;
  suffix: string;
  level: SubscriptionLevel;
  interval: SubscriptionInterval;
  savings_in_percent: number;
  comparable_savings_with_currency: string;
  yearly_price_for_monthly_product: string;
  product: SubscriptionProduct;
};

export type PaginationLink = {
  url: string | null;
  label: string;
  active: boolean;
};

export type Paginated<T> = {
  data: T[];
  links: PaginationLink[];
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
  from: number | null;
  to: number | null;
};

export type SimplePagination<T> = {
  data: T[];
  current_page: number;
  current_page_url: string;
  first_page_url: string;
  from: number | null;
  path: string;
  next_page_url: string | null;
  per_page: number;
  prev_page_url: string | null;
  to: number;
};

export type ModelTranslation = {
  id: number;
  is_active: boolean;
  language: string;
  model_id: number;
  model_type: string;
  property: string;
  value: string | null;
  source: string;
  created_at: string;
  updated_at: string;
};

export type InputType = "text" | "textarea";

export type TranslatableProperty = {
  input_type: InputType;
  property: string;
  is_required: boolean;
};

export type JournalSection = {
  id: string;
  title: string;
  content: string;
  order: number;
  required: boolean;
  children?: JournalSection[];
  parent_id?: string | null;
  previous_section_id?: string | null;
  level?: number;
};

export type DocumentType = {
  id: number;
  name: string;
  description: string;
  prompt_template: string;
  default_enabled: boolean;
  users_count: number;
  usage_count: number;
  created_at: string;
  updated_at: string;
  model_translations: ModelTranslation[];
  professional_roles?: ProfessionalRole[];
};

export type ProfessionalRole = {
  id: number;
  name: string;
  label: string | null;
  is_active: boolean;
  description?: string | null;
  prompt?: string | null;
  users_count?: number;
  created_at?: string;
  updated_at?: string;
  model_translations: ModelTranslation[];
};

export type UserProfessionalRole = {
  id: number;
  name: string;
  label?: string;
  description?: string;
  prompt?: string;
  is_assigned: boolean;
  custom_text?: string;
};

export type SessionDocument = {
  id: number;
  title: string;
  body_text: string;
  status: "pending" | "generating" | "completed" | "failed";
  created_at: string;
  updated_at?: string;
  document_type: {
    id: number;
    key: string;
    name: string;
  };
};

export type SessionDocumentsData = {
  documents: SessionDocument[];
  available_document_types: DocumentType[];
};

export type SummaryComparison = {
  latest_summary: {
    id: number;
    structured_journal: JournalSection[];
    created_at: string;
  };
  previous_summary: {
    id: number;
    structured_journal: JournalSection[];
    created_at: string;
  };
};

type LocalizationItem = {
  value: string;
  label: string;
  flag: string;
};

export type Locale = LocalizationItem & {
  default_language_value: string;
  default_language_label: string;
};

export type Language = LocalizationItem & {
  default_locale_value: string;
  default_locale_label: string;
};

export type OnboardingStepLink = {
  url: string;
  text: string | null;
  is_external: boolean;
};

export type OnboardingStepData = {
  value: string;
  label: string;
  description: string;
  is_completed: boolean;
  is_conditional: boolean;
  is_applicable: boolean;
  can_be_manually_completed: boolean;
  link: OnboardingStepLink | null;
  parent_value: string | null;
  children: OnboardingStepData[];
};

export type OnboardingProgress = {
  steps: OnboardingStepData[];
  progress: number;
  is_complete: boolean;
  has_celebrated: boolean;
};

export type PatientReferenceStyle = "third_person" | "omit_patient";
